import apiClient from '@/api/apiClient';
import type {
  SubscriptionSchema,
  SubscriptionCreateSchema,
  TokenBalanceSchema,
  ManualTopupRequestSchema,
  ManualTopupQuoteSchema,
  PaginatedSubscriptionSchema,
  WalletStatementSchema,
  UsageLedgerStatementSchema,
  PaginatedInvoiceSchema,
  BillingSummarySchema
} from '@/types/billingManagement';

const billingApi = {
  getSummary: async (accountId?: string): Promise<BillingSummarySchema> => {
    return apiClient.get('/billing/summary', { params: { accountId } });
  },

  getActiveSubscription: async (accountId?: string): Promise<SubscriptionSchema> => {
    return apiClient.get('/billing/subscription', { params: { accountId } });
  },

  getSubscriptionHistory: async (
    accountId?: string,
    page: number = 1,
    page_size: number = 50,
    filters: { plan_name?: string } = {},
  ): Promise<PaginatedSubscriptionSchema> => {
    return apiClient.get('/billing/subscriptions', {
      params: { ...filters, page, page_size, accountId },
    });
  },

  createSubscription: async (
    data: SubscriptionCreateSchema,
    accountId?: string,
  ): Promise<SubscriptionSchema> => {
    return apiClient.post('/billing/subscription', data, { params: { accountId } });
  },

  setOverage: async (
    subscriptionId: string,
    overageAllowed: boolean,
    accountId?: string,
  ): Promise<SubscriptionSchema> => {
    return apiClient.patch(
      `/billing/subscription/${subscriptionId}/overage`,
      { overage_allowed: overageAllowed },
      { params: { accountId } },
    );
  },

  getBalance: async (accountId?: string): Promise<TokenBalanceSchema> => {
    return apiClient.get('/billing/balance', { params: { accountId } });
  },

  getManualTopupQuote: async (
    accountId?: string,
    tokenAmount: number = 1000,
  ): Promise<ManualTopupQuoteSchema> => {
    return apiClient.get('/billing/topup/quote', {
      params: { token_amount: tokenAmount, accountId },
    });
  },

  manualTopup: async (
    data: ManualTopupRequestSchema,
    accountId?: string,
  ): Promise<TokenBalanceSchema> => {
    return apiClient.post('/billing/topup', data, { params: { accountId } });
  },

  getWalletHistory: async (
    accountId?: string,
    page: number = 1,
    page_size: number = 50,
  ): Promise<WalletStatementSchema> => {
    return apiClient.get('/billing/wallet', {
      params: { page, page_size, accountId },
    });
  },

  getInvoices: async (accountId?: string, page: number = 1, page_size: number = 50): Promise<PaginatedInvoiceSchema> => {
    return apiClient.get('/billing/invoices', {
      params: { page, page_size, accountId }
    });
  },

  getUsageLedger: async (
    accountId?: string,
    page: number = 1,
    page_size: number = 50,
    filters: {
      product?: string;
      entity_id?: string;
      start_date?: string;
      end_date?: string;
    } = {},
  ): Promise<UsageLedgerStatementSchema> => {
    return apiClient.get('/usage/ledger', {
      params: {
        page,
        page_size,
        ...filters,
        accountId,
      },
    });
  },
};

export default billingApi;
